import { Box, Chip, CircularProgress, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { ReactNode } from "react";
import { BundleDetail, FinishedGroup } from "../../models/FinishedBundle";

export const FILTERS: {
	key: "active" | "all" | "review" | "manual";
	label: string;
	always: boolean;
}[] = [
	{ key: "active", label: "Bundles", always: true },
	{ key: "all", label: "All", always: true },
	{ key: "review", label: "To review", always: false },
	{ key: "manual", label: "Manual", always: false },
];

// To review: flagged by the tracking and nobody reviewed it (mill stopped / heat to check), or active with no heat
export const needsReview = (b: BundleDetail) =>
	b.MillStoppedToCheck || b.HeatToCheck || (b.IsActive && b.HeatId == null);

export const matchesFilter = (
	b: BundleDetail,
	filter: "active" | "all" | "review" | "manual",
) => {
	switch (filter) {
		case "review":
			return needsReview(b);
		case "manual":
			return b.IsManual;
		case "active":
			return b.IsActive;
		default:
			return true;
	}
};

// checkable = the flag can be cleared with "Checked" (the values are right); a bundle with no heat needs one assigned
export const getBundleStatus = (
	b: BundleDetail,
): {
	label: string;
	tone: "success" | "warning" | "info" | "neutral";
	tooltip?: string;
	checkable?: boolean;
} | null => {
	if (!b.IsActive) return { label: "Deleted", tone: "neutral" };
	// No heat linked: the PLC heat wasn't rolling on Mill #2 at the bundle time and the mill was stopped
	if (b.HeatId == null)
		return {
			label: "Check heat",
			tone: "warning",
			tooltip:
				b.PlcHeatNumber != null
					? `The PLC says heat ${b.PlcHeatNumber}, but it wasn't rolling on Mill #2 at that time: assign the heat`
					: "The PLC sent no heat: assign the heat",
		};
	if (b.MillStoppedToCheck)
		return {
			label: "Mill stopped",
			tone: "warning",
			checkable: true,
			tooltip:
				"Detected with the mill stopped: delete it if it isn't a real bundle, or ✓ if it is",
		};
	if (b.HeatToCheck) {
		// The tracking took the heat of the last billet rolled when the PLC heat wasn't rolling (16-bit register:
		// the heat can be the PLC number + 65536)
		const plcHeat =
			b.PlcHeatNumber != null &&
			b.HeatName !== String(b.PlcHeatNumber) &&
			b.HeatName !== String(b.PlcHeatNumber + 65536);
		return {
			label: "Check heat",
			tone: "warning",
			checkable: true,
			tooltip: plcHeat
				? `The PLC says heat ${b.PlcHeatNumber}, but it wasn't rolling on Mill #2 at that time: heat ${b.HeatName} (the last one rolled) was assigned. Edit it, or ✓ if it's right`
				: "The heat had no billet rolling in the 2 h before the bundle. Edit it, or ✓ if it's right",
		};
	}
	return null;
};

export const suggestHeatIds = (
	targets: BundleDetail[],
	shiftBundles: BundleDetail[],
) => {
	const withHeat = shiftBundles.filter((b) => b.HeatId != null);
	const nearest = (target: BundleDetail) => {
		const time = new Date(target.Timestamp).getTime();
		const sameSection = withHeat.filter(
			(b) => b.SectionId === target.SectionId,
		);
		const pool = sameSection.length ? sameSection : withHeat;
		const before = pool
			.filter((b) => new Date(b.Timestamp).getTime() <= time)
			.sort(
				(a, b) =>
					new Date(b.Timestamp).getTime() - new Date(a.Timestamp).getTime(),
			)[0];
		const after = pool
			.filter((b) => new Date(b.Timestamp).getTime() > time)
			.sort(
				(a, b) =>
					new Date(a.Timestamp).getTime() - new Date(b.Timestamp).getTime(),
			)[0];
		return [before, after]
			.filter((b): b is BundleDetail => !!b)
			.sort(
				(a, b) =>
					Math.abs(new Date(a.Timestamp).getTime() - time) -
					Math.abs(new Date(b.Timestamp).getTime() - time),
			)
			.map((b) => b.HeatId as number);
	};
	return [...new Set(targets.filter((t) => t.HeatId == null).flatMap(nearest))];
};

export type HeatGroup = {
	key: string;
	SectionId: number;
	SectionName: string;
	HeatId: number | null;
	HeatName: string | null;
	GradeName: string | null;
	LengthFt: number;
	LengthInches: number;
	bundles: number;
	pieces: number;
	weight: number;
	pending: number;
	suspicious: number;
	excluded: number;
	scrapPieces: number;
	breakdown: string | null;
	piecesPerBundle: number;
	scrapKey: string | null;
};

export const toHeatGroups = (groups: FinishedGroup[]): HeatGroup[] =>
	groups.map((g) => ({
		key: g.GroupKey,
		SectionId: g.SectionId,
		SectionName: g.SectionName,
		HeatId: g.HeatId,
		HeatName: g.HeatName,
		GradeName: g.GradeName,
		LengthFt: g.LengthFt,
		LengthInches: g.LengthInches,
		bundles: g.GoodBundles || 0,
		pieces: g.TotalPieces || 0,
		weight: g.TotalWeight || 0,
		pending: g.PendingBundles || 0,
		suspicious: g.HeatSuspiciousBundles || 0,
		excluded: g.ExcludedBundles || 0,
		scrapPieces: g.ScrapPieces || 0,
		breakdown: g.BundlesBreakdown,
		piecesPerBundle: g.GoodPiecesPerBundle || 0,
		scrapKey: g.ScrapKey,
	}));

export const Pill = ({
	label,
	tone,
	filled = false,
}: {
	label: string;
	tone: "success" | "warning" | "info" | "neutral";
	filled?: boolean;
}) => (
	<Chip
		size="small"
		label={label}
		variant="outlined"
		sx={(t) => {
			const main =
				tone === "neutral" ? t.palette.text.secondary : t.palette[tone].main;
			const text =
				tone === "neutral" ? t.palette.text.secondary : t.palette[tone].dark;
			return {
				fontWeight: 500,
				height: 22,
				flexShrink: 0,
				color: text,
				borderColor: filled ? "transparent" : alpha(main, 0.6),
				backgroundColor: filled ? alpha(main, 0.12) : alpha(main, 0.06),
			};
		}}
	/>
);

export const SummaryItem = ({
	label,
	value,
	prefix,
	loading,
	testId,
}: {
	label: string;
	value: string;
	prefix?: ReactNode;
	loading: boolean;
	testId?: string;
}) => (
	<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
		{prefix}
		<Typography
			variant="caption"
			color="text.secondary"
			sx={{ fontWeight: 500 }}
		>
			{label}:
		</Typography>
		{loading ? (
			<CircularProgress size={18} thickness={5} />
		) : (
			<Typography
				variant="h6"
				color="primary.main"
				sx={{ fontWeight: 500, fontVariantNumeric: "tabular-nums" }}
				data-testid={testId}
			>
				{value}
			</Typography>
		)}
	</Box>
);
